import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiCall } from '../../services/api.service';

interface WorkspacePreferences {
  hidden_ids: string[];
  pinned_order: string[];
}

interface WorkspaceContextValue {
  getPrefs: (context: string) => WorkspacePreferences;
  isVisible: (context: string, id: string) => boolean;
  toggle: (context: string, id: string) => Promise<void>;
  getPinnedOrder: (context: string) => string[];
  setPinnedOrder: (context: string, order: string[]) => Promise<void>;
  hiddenCount: (context: string) => number;
  ready: boolean;
}

const defaultPrefs: WorkspacePreferences = { hidden_ids: [], pinned_order: [] };

const WorkspaceContext = createContext<WorkspaceContextValue>({
  getPrefs: () => defaultPrefs,
  isVisible: () => true,
  toggle: async () => {},
  getPinnedOrder: () => [],
  setPinnedOrder: async () => {},
  hiddenCount: () => 0,
  ready: false,
});

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [prefsMap, setPrefsMap] = useState<Record<string, WorkspacePreferences>>({});
  const [ready, setReady] = useState(false);
  const loadedContexts = useRef<Set<string>>(new Set());

  useEffect(() => {
    const PUBLIC_PREFIXES = ['/onboarding', '/pub', '/flows', '/firebase-login', '/store/'];
    const isPublicRoute = PUBLIC_PREFIXES.some(p => window.location.pathname.startsWith(p));
    if (isPublicRoute) {
      setReady(true);
      return;
    }
    apiCall('user/workspace/preferences')
      .then((rows: Array<{ context: string; hidden_ids: string[]; pinned_order: string[] }>) => {
        const map: Record<string, WorkspacePreferences> = {};
        for (const row of rows) {
          map[row.context] = { hidden_ids: row.hidden_ids, pinned_order: row.pinned_order };
          loadedContexts.current.add(row.context);
        }
        setPrefsMap(map);
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const getPrefs = useCallback(
    (context: string): WorkspacePreferences => prefsMap[context] ?? defaultPrefs,
    [prefsMap],
  );

  const isVisible = useCallback(
    (context: string, id: string): boolean => {
      const prefs = prefsMap[context];
      if (!prefs) return true;
      return !prefs.hidden_ids.includes(id);
    },
    [prefsMap],
  );

  const toggle = useCallback(
    async (context: string, id: string) => {
      const current = prefsMap[context] ?? defaultPrefs;
      const hidden = current.hidden_ids.includes(id)
        ? current.hidden_ids.filter((x) => x !== id)
        : [...current.hidden_ids, id];
      const next: WorkspacePreferences = { ...current, hidden_ids: hidden };
      setPrefsMap((prev) => ({ ...prev, [context]: next }));
      await apiCall(`user/workspace/preferences/${context}`, {
        method: 'PUT',
        body: JSON.stringify({ hidden_ids: hidden, pinned_order: next.pinned_order }),
      }).catch(() => {
        setPrefsMap((prev) => ({ ...prev, [context]: current }));
      });
    },
    [prefsMap],
  );

  const getPinnedOrder = useCallback(
    (context: string): string[] => prefsMap[context]?.pinned_order ?? [],
    [prefsMap],
  );

  const setPinnedOrder = useCallback(
    async (context: string, order: string[]) => {
      const current = prefsMap[context] ?? defaultPrefs;
      const next: WorkspacePreferences = { ...current, pinned_order: order };
      setPrefsMap((prev) => ({ ...prev, [context]: next }));
      await apiCall(`user/workspace/preferences/${context}`, {
        method: 'PUT',
        body: JSON.stringify({ hidden_ids: next.hidden_ids, pinned_order: order }),
      }).catch(() => {
        setPrefsMap((prev) => ({ ...prev, [context]: current }));
      });
    },
    [prefsMap],
  );

  const hiddenCount = useCallback(
    (context: string): number => prefsMap[context]?.hidden_ids.length ?? 0,
    [prefsMap],
  );

  return (
    <WorkspaceContext.Provider
      value={{ getPrefs, isVisible, toggle, getPinnedOrder, setPinnedOrder, hiddenCount, ready }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaceContext() {
  return useContext(WorkspaceContext);
}
